import type {
  CameraConfig,
  EnvironmentConfig,
  LightConfig,
  PostProcessConfig,
} from '../../engine/types';

const ASSET_BASE = 'assets/3d/magic-ball';

export const glbPath = `${ASSET_BASE}/magic-ball_isolated.glb`;

export const meshNames = {
  ball: 'ball',
  icosahedron: 'icosahedron',
  window: 'window',
} as const;

export const cameraConfig: CameraConfig = {
  fov: 45,
  near: 0.01,
  far: 100,
  position: [-5.4120, 1.8986, -8.1549],
  target: [-0.0431, 0.2256, -0.0285],
  controls: {
    enableDamping: true,
    dampingFactor: 0.05,
    minDistance: 2,
    maxDistance: 15,
    autoRotate: false,
    autoRotateSpeed: 1.0,
    enablePan: false,
  },
};

export const lights: LightConfig[] = [
  {
    type: 'point',
    color: 0xffffff,
    intensity: 4,
    position: [3, 4, 5],
  },
  {
    type: 'point',
    color: 0xaaccff,
    intensity: 1.5,
    position: [-4, 2, 3],
  },
  {
    type: 'point',
    color: 0xffffff,
    intensity: 2,
    position: [-1, 3, -5],
  },
  {
    type: 'ambient',
    color: 0x111122,
    intensity: 0.3,
  },
];

export const environment: EnvironmentConfig = {
  type: 'hdri',
  path: `${ASSET_BASE}/nebula.hdr`,
  intensity: 0.3,
  background: true,
};

export const envFadeDuration = 2.0;

export const environmentPresets: Record<string, string | null> = {
  studio: 'assets/3d/magic-ball/studio.hdr',
  sunset: 'assets/3d/magic-ball/sunset.hdr',
  night: 'assets/3d/magic-ball/night.hdr',
  warehouse: 'assets/3d/magic-ball/warehouse.hdr',
  citrusOrchard: 'assets/3d/magic-ball/citrus_orchard_road.hdr',
  partlyCloudy: 'assets/3d/magic-ball/kloofendal_38d_partly_cloudy.hdr',
  overcast: 'assets/3d/magic-ball/kloofendal_overcast.hdr',
  qwantaniDusk: 'assets/3d/magic-ball/qwantani_dusk_2.hdr',
  hornKoppeSpring: 'assets/3d/magic-ball/horn-koppe_spring.hdr',
  newmanLobby: 'assets/3d/magic-ball/newman_lobby.hdr',
  kloofendal48d: 'assets/3d/magic-ball/kloofendal_48d_partly_cloudy.hdr',
  glasshouse: 'assets/3d/magic-ball/glasshouse_interior.hdr',
  ferndaleStudio06: 'assets/3d/magic-ball/ferndale_studio_06.hdr',
  ferndaleStudio02: 'assets/3d/magic-ball/ferndale_studio_02.hdr',
  milkyWay: 'assets/3d/magic-ball/milky_way.jpg',
  starfield: 'assets/3d/magic-ball/starfield.jpg',
  nebula: 'assets/3d/magic-ball/nebula.hdr',
  nebulaFlat: 'assets/3d/magic-ball/nebula.jpg',
  none: null,
};

export const accelerometer = {
  shakeThreshold: 15,
  shakeCount: 3,
  shakeWindow: 500,
  tiltSensitivity: 0.08,
  tiltSmoothing: 5,
};

export const ballOrientation: [number, number, number, number] = [-0.444929, 0.030186, 0.267027, 0.854297];

// Positive = ball appears toward screen right; negative = screen left
export const ballScreenShift = 0;

export const cameraIntro = {
  startScale: 3.5,                      // start distance = final distance × this
  duration: 1.8,                        // seconds
  bezier: [0.34, 1.56, 0.64, 1] as const, // overshoot back-ease: fluid with a spunky settle
};

export const physics = {
  icoSpringSpeed: 6,
  ballReturnSpeed: 3,
  dragSpeed: 0.008,
};

export const icoAnimation = {
  restY: 1.3513,
  hiddenY: 0.8,
  sinkDuration: 0.4,
  rotateDuration: 0.2,
  riseDuration: 0.6,
};

export const faceMappings = [
  { message: 'It is certain', quaternion: [0.305917, 0.499992, 0.752070, 0.301353] as const },
  { message: 'It is decidedly so', quaternion: [0.287386, 0.640878, 0.482294, 0.523525] as const },
  { message: 'Without a doubt', quaternion: [0.930941, -0.175243, -0.001222, 0.320371] as const },
  { message: 'Yes definitely', quaternion: [-0.758811, 0.579969, 0.295352, -0.024671] as const },
  { message: 'You may rely on it', quaternion: [0.930573, 0.365445, -0.020934, 0.006724] as const },
  { message: 'As I see it, yes', quaternion: [-0.334270, 0.789722, -0.184267, 0.480258] as const },
  { message: 'Most likely', quaternion: [0.498156, -0.495997, 0.319381, 0.635471] as const },
  { message: 'Outlook good', quaternion: [0.586236, -0.637534, 0.001753, 0.499875] as const },
  { message: 'Yes', quaternion: [-0.310013, 0.523861, 0.739271, 0.287993] as const },
  { message: 'Signs point to yes', quaternion: [-0.750277, -0.264007, 0.305189, -0.523684] as const },
  { message: 'Reply hazy, try again', quaternion: [-0.270160, -0.103556, -0.494836, 0.819407] as const },
  { message: 'Ask again later', quaternion: [0.809435, 0.016639, 0.460445, -0.364046] as const },
  { message: 'Better not tell you now', quaternion: [-0.375794, -0.490666, -0.027384, 0.785669] as const },
  { message: 'Cannot predict now', quaternion: [0.294761, -0.000274, -0.759184, 0.580307] as const },
  { message: 'Concentrate and ask again', quaternion: [0.000000, 0.000000, 0.000000, 1.000000] as const },
  { message: "Don't count on it", quaternion: [-0.513699, -0.327207, -0.261177, 0.748889] as const },
  { message: 'My reply is no', quaternion: [0.653071, 0.567418, -0.501205, 0.018146] as const },
  { message: 'My sources say no', quaternion: [0.350362, 0.468747, -0.020933, 0.810608] as const },
  { message: 'Outlook not so good', quaternion: [0.273424, 0.645056, -0.502219, -0.506871] as const },
  { message: 'Very doubtful', quaternion: [0.999661, 0.013334, 0.006315, 0.021464] as const },
];

export const messages: string[] = faceMappings.map((f) => f.message);

export const postProcess: PostProcessConfig = {
  bloom: {
    threshold: 2.5,
    strength: 0.4,
    radius: 0.8,
  },
  fxaa: true,
  toneMapping: {
    exposure: 0.6,
  },
};
